import { ref } from 'vue'
import { APP_ID } from '@web/common/config'
import Logger from '@web/common/Logger'
import { isEmbedded } from '@web/composition/useEmbedMode'
import { modalBuilderClosed, modalBuilderCtaClick, modalBuilderShown } from '@web/services/Analytics/events/modalBuilder'
import { runAction } from './actions'
import type { ActionHandlers } from './actions'
import { fetchModalRules } from './api'
import { RULES_HOLD_TIMEOUT_MS } from './constants'
import { preloadImage } from './imagePreload'
import { QUEUE_PRIORITY, modalQueue } from './modalQueue'
import { applyClose, applyShown } from './repeatPolicy'
import { readState, writeState } from './repeatState'
import { selectRule } from './selectRule'
import type { CloseMethod, ModalButton, ModalRule, ModalTrigger, RepeatState, TriggerContext } from './types'

export const MODAL_BUILDER_QUEUE_ID = 'modal-builder'
export const MODAL_BUILDER_SESSION_KEY = `modal_builder_shown_${APP_ID}`

export interface ModalBuilderDeps extends ActionHandlers {
  fetchRules: () => Promise<ModalRule[]>
  loadImage: (url: string) => Promise<boolean>
  isKnownRoute: (path: string) => boolean
  now: () => number
  rulesHoldTimeoutMs: number
}

function defaultDeps (): ModalBuilderDeps {
  return {
    fetchRules: fetchModalRules,
    loadImage: preloadImage,
    // До подключения роутера хостом deeplink считаем неизвестным — правило не покажется.
    isKnownRoute: () => false,
    now: () => Date.now(),
    rulesHoldTimeoutMs: RULES_HOLD_TIMEOUT_MS,
    openUrl: url => { window.open(url, '_blank', 'noopener') },
    assignUrl: url => { window.location.assign(url) },
    navigate: () => undefined
  }
}

let deps = defaultDeps()

export function configureModalBuilder (partial: Partial<ModalBuilderDeps>): void {
  deps = { ...deps, ...partial }
}

// Синглтон-шина: один хост в дереве, страницы зовут trigger.
const rules = ref<ModalRule[]>([])
const current = ref<ModalRule | null>(null)
let userId: number | null = null
let loading: Promise<void> | null = null
let shownPromise: Promise<void> | null = null

function sessionUsed (): boolean {
  try { return sessionStorage.getItem(MODAL_BUILDER_SESSION_KEY) === '1' } catch { return false }
}

function setSessionUsed (used: boolean): void {
  try {
    if (used) sessionStorage.setItem(MODAL_BUILDER_SESSION_KEY, '1')
    else sessionStorage.removeItem(MODAL_BUILDER_SESSION_KEY)
  } catch { /* приватный режим: просто не помним */ }
}

export function resetModalBuilder (): void {
  deps = defaultDeps()
  rules.value = []
  current.value = null
  userId = null
  loading = null
  shownPromise = null
}

/** true — список загрузился за rulesHoldTimeoutMs, false — время вышло. */
function settledInTime (promise: Promise<void>): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(false), deps.rulesHoldTimeoutMs) })
  return Promise.race([promise.then(() => true), timeout]).finally(() => clearTimeout(timer))
}

// Выход или смена аккаунта: модалка прежнего пользователя исчезает молча —
// это не его закрытие, поэтому ни состояния, ни событий. Заявка, ещё ждущая
// в очереди, снимается тоже — иначе откроется уже после выхода.
function dropCurrent (): void {
  current.value = null
  shownPromise = null
  modalQueue.release(MODAL_BUILDER_QUEUE_ID)
}

function load (nextUserId: number | null): Promise<void> {
  if (nextUserId === userId && loading) return loading
  if (nextUserId !== userId) dropCurrent()
  // Другой аккаунт в той же вкладке — для него это новая сессия.
  if (userId !== null && nextUserId !== userId) setSessionUsed(false)
  userId = nextUserId
  rules.value = []
  if (nextUserId === null) {
    loading = null
    return Promise.resolve()
  }

  const unhold = modalQueue.hold()
  // Зависший запрос не должен держать очередь: DAY-1 откроется по таймауту.
  const holdTimer = setTimeout(unhold, deps.rulesHoldTimeoutMs)
  const forUser = nextUserId
  loading = Promise.resolve().then(() => deps.fetchRules())
    .then(list => {
      if (userId !== forUser) return
      rules.value = list
      list.forEach(r => { if (r.content.imageUrl) deps.loadImage(r.content.imageUrl) })
    })
    .catch(e => { Logger.warn('[modal-builder] загрузка правил', e) })
    .finally(() => {
      clearTimeout(holdTimer)
      unhold()
    })
  return loading
}

// Тот же пользователь: открытая модалка и флаг вкладки остаются, меняется только список.
function reload (): Promise<void> {
  loading = null
  return load(userId)
}

async function trigger (name: ModalTrigger, ctx: TriggerContext = {}): Promise<void> {
  if (isEmbedded() || sessionUsed() || !loading) return
  const unhold = modalQueue.hold()
  try {
    // Список не успел — этот вход без серверной модалки, очередь не держим.
    if (!await settledInTime(loading)) return
    const forUser = userId
    if (forUser === null || sessionUsed() || current.value) return

    const candidates = rules.value.filter(r => r.trigger === name)
    const entries = await Promise.all(candidates.map(async r => [r.modalId, await readState(forUser, r.modalId)] as const))
    const states = new Map<number, RepeatState>(entries)
    const rule = selectRule(rules.value, name, ctx, states, deps.now(), deps.isKnownRoute)
    if (!rule || userId !== forUser || sessionUsed()) return

    setSessionUsed(true)
    modalQueue.enqueue({
      id: MODAL_BUILDER_QUEUE_ID,
      priority: QUEUE_PRIORITY.server,
      open: () => {
        shownPromise = null
        current.value = rule
      }
    })
  } catch (e) {
    Logger.warn('[modal-builder] trigger', e)
  } finally {
    unhold()
  }
}

// Вызывается компонентом, когда видны текст и кнопки (не скелетон).
function markShown (): Promise<void> {
  const rule = current.value
  const forUser = userId
  if (!rule || forUser === null || shownPromise) return shownPromise ?? Promise.resolve()
  shownPromise = (async () => {
    const state = applyShown(await readState(forUser, rule.modalId), deps.now())
    await writeState(forUser, rule.modalId, state)
    modalBuilderShown({ rule, showIndex: state.showCount })
  })()
  return shownPromise
}

async function close (method: CloseMethod): Promise<void> {
  const rule = current.value
  const forUser = userId
  const shown = shownPromise
  if (!rule) return
  current.value = null
  shownPromise = null
  modalQueue.release(MODAL_BUILDER_QUEUE_ID)
  // Закрыли из скелетона — показ не засчитан, событий нет.
  if (!shown || forUser === null) return
  await shown
  await writeState(forUser, rule.modalId, applyClose(await readState(forUser, rule.modalId), method))
  modalBuilderClosed({ rule, closeMethod: method })
}

async function clickButton (button: ModalButton): Promise<void> {
  const rule = current.value
  if (!rule) return
  if (shownPromise) modalBuilderCtaClick({ rule, buttonId: button.id })
  const closing = close(button.action === 'close' ? 'close' : 'cta')
  // Новую вкладку открываем синхронно в обработчике клика — иначе её заблокирует браузер.
  if (button.action === 'url') {
    runAction(button, deps)
    await closing
    return
  }
  // Переход в той же вкладке — после записи состояния, чтобы она не потерялась.
  await closing
  runAction(button, deps)
}

export function useModalBuilder () {
  return { rules, current, load, reload, trigger, markShown, close, clickButton }
}
